/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */
import { css, jsx } from '@emotion/react'
import { filter, noop } from 'lodash'
import { Component, Fragment } from 'react'
import { injectIntl, defineMessages } from 'react-intl'

import {
  EuiFlexGroup,
  EuiFlexItem,
  EuiFormLabel,
  EuiIconTip,
  EuiButtonEmpty,
  EuiLoadingSpinner,
  EuiSpacer,
  EuiText,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import { withErrorBoundary } from '@modules/cui/Boundary'
import { CuiHelpTipIcon } from '@modules/cui/HelpTipIcon'
import { CuiAlert } from '@modules/cui/Alert'
import PermissionsGate from '@modules/permissions-components/PermissionsGate'
import { DocLink } from '@modules/doclinks'

import { hasMismatchingVersions } from '@/lib/stackDeployments/upgradesToSliders'
import { isSystemOwned } from '@/lib/stackDeployments/selectors/metadata'
import { getRegionId, getVersion } from '@/lib/stackDeployments/selectors/fundamentals'
import { satisfies } from '@/lib/semver'

import DeploymentLockGate from '../../../DeploymentLockGate'

import PartialUpgradeTooltip from './PartialUpgradeTooltip'
import DeploymentVersionUpgradeModal from './DeploymentVersionUpgradeModal'

import type { AllProps } from './types'

const upgradableDeploymentVersionHiddenStyle = css({
  visibility: 'hidden', // preserve vertical space
})

const messages = defineMessages({
  ariaLabel: {
    id: `upgradable-deployment-version.aria`,
    defaultMessage: `More information`,
  },
})

type State = {
  showUpgradeModal: boolean
  versionToUpgradeToExists: boolean
}

class UpgradableDeploymentVersion extends Component<AllProps, State> {
  state: State = {
    showUpgradeModal: false,
    versionToUpgradeToExists: false,
  }

  async componentDidMount() {
    const { showModalOnMount, deployment, fetchVersionStack, lowestVersion } = this.props

    // If a deployment is partially upgraded, we pick the lowest
    // found version found in each resource to upgrade from.
    const regionId = getRegionId({ deployment })

    if (!lowestVersion) {
      return
    }

    fetchVersionStack(lowestVersion, regionId)
      .then(({ payload }) => {
        if (showModalOnMount) {
          if (payload?.upgradable_to.length) {
            this.setState({ versionToUpgradeToExists: true })
            this.showUpgrade()
          }
        }
      })
      .catch(noop)
  }

  render() {
    const { deployment, versionStack } = this.props
    const version = getVersion({ deployment })

    if (version == null) {
      return null
    }

    const isVersionDeleted = versionStack != null ? versionStack.deleted : false
    const { versionToUpgradeToExists } = this.state
    return (
      <div>
        <EuiFormLabel>
          <EuiFlexGroup gutterSize='none' alignItems='center'>
            <EuiFlexItem grow={false}>
              <FormattedMessage
                id='upgradable-deployment-version.deployment-version'
                defaultMessage='Deployment version'
              />
            </EuiFlexItem>

            {isVersionDeleted && versionToUpgradeToExists && (
              <EuiFlexItem grow={false}>
                <span
                  data-test-id='upgradable-deployment-version-deleted'
                  style={{ verticalAlign: `text-bottom`, marginLeft: 4 }}
                >
                  <EuiIconTip
                    size='m'
                    aria-label='Version deleted'
                    content={
                      <FormattedMessage
                        id='upgradable-deployment-version.version-deleted'
                        defaultMessage='This Elastic Stack version has been deleted. Please upgrade to a new version.'
                      />
                    }
                    type='alert'
                    color='warning'
                  />
                </span>
              </EuiFlexItem>
            )}
          </EuiFlexGroup>
        </EuiFormLabel>
        <EuiSpacer size='s' />

        {this.renderContent()}
      </div>
    )
  }

  renderContent() {
    const { deployment, availableVersions, esVersionRequest } = this.props
    const version = getVersion({ deployment })!

    if (esVersionRequest.error) {
      return (
        <Fragment>
          <EuiSpacer size='s' />
          <CuiAlert type='error' data-test-id='es-version-request-error'>
            {esVersionRequest.error}
          </CuiAlert>
        </Fragment>
      )
    }

    if (availableVersions == null) {
      return (
        <Fragment>
          <EuiSpacer size='s' />
          <EuiLoadingSpinner />
        </Fragment>
      )
    }

    const upgradeAvailable = this.getAvailableVersions().length > 0

    return (
      <Fragment>
        <EuiFlexGroup gutterSize='m' alignItems='center' responsive={false}>
          <EuiFlexItem grow={false} data-test-id='deployment-version'>
            <EuiText size='s'>
              {
                // tslint:disable-next-line:jsx-use-translation-function
                `v${version}`
              }
            </EuiText>
          </EuiFlexItem>

          <DeploymentLockGate>{this.renderStartUpgradeButton(upgradeAvailable)}</DeploymentLockGate>
        </EuiFlexGroup>

        {this.renderUpgradeModal()}
      </Fragment>
    )
  }

  renderStartUpgradeButton(anySelectableVersions) {
    const {
      deployment,
      intl: { formatMessage },
    } = this.props
    const { showUpgradeModal } = this.state

    const isHidden = showUpgradeModal || !anySelectableVersions
    const isUpgradeIncomplete = hasMismatchingVersions({ deployment })

    return (
      <EuiFlexItem
        grow={false}
        className={isHidden ? 'upgradableDeploymentVersion-hidden' : undefined}
        css={isHidden ? upgradableDeploymentVersionHiddenStyle : undefined}
        aria-hidden={isHidden}
      >
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <EuiFlexGroup gutterSize='xs' alignItems='baseline' responsive={false}>
            <EuiFlexItem grow={false}>
              {isUpgradeIncomplete ? (
                <PartialUpgradeTooltip deployment={deployment} />
              ) : (
                <CuiHelpTipIcon aria-label={formatMessage(messages.ariaLabel)} color='primary'>
                  <span data-test-id='upgrade-available'>
                    <FormattedMessage
                      id='upgradable-deployment-version.upgrade-available'
                      defaultMessage='There is a newer version available. {learnMore}'
                      values={{
                        learnMore: (
                          <DocLink link='upgradingDocLink'>
                            <FormattedMessage
                              id='upgradable-deployment-version.upgrade-available-link'
                              defaultMessage='Learn more'
                            />
                          </DocLink>
                        ),
                      }}
                    />
                  </span>
                </CuiHelpTipIcon>
              )}
            </EuiFlexItem>
            <EuiFlexItem grow={false}>
              <PermissionsGate
                permissions={[
                  {
                    type: 'deployment',
                    action: 'upgrade',
                    id: deployment.id,
                  },
                ]}
              >
                {({ hasPermissions }) => (
                  <EuiButtonEmpty
                    flush='both'
                    disabled={!hasPermissions}
                    onClick={() => this.showUpgrade()}
                    data-test-id='upgradable-deployment-version-show-available'
                  >
                    <FormattedMessage
                      id='upgradable-deployment-version.upgrade-link'
                      defaultMessage='Upgrade'
                    />
                  </EuiButtonEmpty>
                )}
              </PermissionsGate>
            </EuiFlexItem>
          </EuiFlexGroup>
        </div>
      </EuiFlexItem>
    )
  }

  renderUpgradeModal() {
    const { deployment, region } = this.props
    const { showUpgradeModal } = this.state

    if (!showUpgradeModal) {
      return null
    }

    return (
      <DeploymentVersionUpgradeModal
        deployment={deployment}
        isLoadingRegion={!region}
        onCancel={this.cancelShowUpgrade}
      />
    )
  }

  showUpgrade() {
    const { deployment, fetchRegion, fetchVersionStacks } = this.props

    const regionId = getRegionId({ deployment })!

    fetchRegion(regionId)
    fetchVersionStacks(regionId)

    this.setState({ showUpgradeModal: true })
  }

  cancelShowUpgrade = () => {
    this.setState({ showUpgradeModal: false })
  }

  getAvailableVersions = () => {
    const { deployment, availableVersions } = this.props

    if (!availableVersions) {
      return []
    }

    // temporary fix, still waiting for the API to provide upgrade available for system owned deployment
    // see https://github.com/elastic/cloud/issues/31782
    return isSystemOwned({ deployment })
      ? filter(availableVersions, (version) => satisfies(version, `<7`))
      : availableVersions
  }
}

export default withErrorBoundary(injectIntl(UpgradableDeploymentVersion))
